import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import classNames from 'classnames';
import { Button } from 'components/ui/button';
import { Icon } from 'components/ui/icon';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/ui/tooltip';
import ModalContainer from 'components/modals/container';
import BannerContainer, { BannerType } from 'components/banner';
import usePartnerStores from 'state/partnerStores/hooks/usePartnerStores';
import CopyToClipboard from 'react-copy-to-clipboard';
import RadioButtonCard, { IRadioConfig } from 'components/card-radio-input';
import { PARTNER_STORES } from 'state/partnerStores/type';
import usePayment from 'state/payment/hooks/usePayment';
import { PAYMENTS } from 'state/payment/type';
import { downloadFile } from 'utils/download';
interface IProps {
  openModal: boolean;
  setModal: Dispatch<SetStateAction<boolean>>;
  isPartner: boolean;
}

enum APIType {
  PARTNERS = 'partners',
  SELF = 'self',
}

enum STAGE {
  SELECT_TYPE,
  LIST_PARTNERS,
  GET_KEYS,
}

const errorBanner = (
  <BannerContainer
    heading="An unexpected error occured"
    message="We are unable to create API keys at the moment. Please close and try again."
    showBanner
    type={BannerType.error}
    listOfError={false}
    fixedHeight={false}
    testId="error-create-keys"
  />
);

const SelectKeyType = ({
  type,
  setType,
}: {
  type: APIType | null;
  setType: React.Dispatch<React.SetStateAction<APIType | null>>;
}) => {
  const {
    partnerStores: { error: partnerErr },
  } = usePartnerStores();
  const {
    payment: { error: keyError },
  } = usePayment();

  const radioOptions = [
    {
      detailsElement: (
        <>
          <div className="text-gray-blue-80 body-subtext-semibold">Partner</div>
          <div className="text-gray-blue-60 mt-1 footnote">
            Create key for a partner
          </div>
        </>
      ),
      value: APIType.PARTNERS,
      selected: type === APIType.PARTNERS,
      onChangeHandler: (e: string) => {
        setType(e as APIType);
      },
      label: APIType.PARTNERS,
    },
    {
      detailsElement: (
        <>
          <div className="text-gray-blue-80 body-subtext-semibold">Self</div>
          <div className="text-gray-blue-60 mt-1 footnote">
            Create key for self
          </div>
        </>
      ),
      value: APIType.SELF,
      selected: type === APIType.SELF,
      onChangeHandler: (e: string) => {
        setType(e as APIType);
      },
      label: APIType.SELF,
    },
  ];

  const showError = () => {
    return (
      (type === APIType.PARTNERS && partnerErr?.fetchPartners) ||
      (type === APIType.SELF && keyError?.addKeySelf)
    );
  };

  return (
    <>
      {showError() && errorBanner}
      <div className="mt-1">
        You can either create API keys for a Partner or for Self.
      </div>
      <div className="flex mt-5">
        {radioOptions.map((option: IRadioConfig) => (
          <RadioButtonCard
            config={option}
            name="APIKeyType"
            cardStyles={{ marginRight: '24px' }}
          />
        ))}
      </div>
      <div className="flex mt-4">
        <span>
          <Icon
            name="info"
            size={18}
            className="pr-2"
            style={{ paddingTop: '2px', color: '#808690' }}
          />
        </span>
        <span>
          Partner API keys have a restricted scope, which allows them to manage
          only the resources they have created.
        </span>
      </div>
    </>
  );
};

const ListPartners = ({
  selectedPartner,
  setSelectedPartner,
}: {
  selectedPartner: string | null;
  setSelectedPartner: React.Dispatch<React.SetStateAction<string | null>>;
}) => {
  const { partnerStores } = usePartnerStores();
  const { partners } = partnerStores;
  const {
    payment: { error: keyError },
  } = usePayment();

  const getRadioConfig = (partner: PARTNER_STORES.IPartner) => {
    return {
      detailsElement: (
        <>
          <div className="text-gray-blue-80 body-subtext-semibold">
            {partner.name}
          </div>
          <div style={{ width: '124px', height: '28px' }} className="mt-2">
            {partner?.image ? (
              <img
                style={{ maxWidth: '100%', maxHeight: '100%' }}
                src={partner.image}
                alt={`${partner.name} logo`}
              />
            ) : (
              <div className="w-8 h-8 bg-gray-blue-4 flex items-center justify-center rounded-sm">
                <Icon
                  name="handshake"
                  aria-label="Partner icon"
                  className="text-gray-blue-60"
                />
              </div>
            )}
          </div>
        </>
      ),
      value: partner.id,
      selected: partner.id === selectedPartner,
      onChangeHandler: (e: string) => {
        setSelectedPartner(e);
      },
      label: partner.name,
    };
  };

  return (
    <>
      {keyError?.addKeyPartner ? (
        errorBanner
      ) : partners?.items?.length === 0 ? (
        <div className="body-subtext mt-6 mb-6 text-center">
          No partners to select. Please go back and select Self option if that
          is applicable.
        </div>
      ) : (
        <>
          <div className="body-subtext-medium" style={{ color: '#333D4E' }}>
            Select a Partner
          </div>
          <div
            className={classNames(
              'mt-5',
              'w-full grid grid-cols-2 grid-rows-[repeat(auto-fill,1fr)] gap-x-4 gap-y-6'
            )}
          >
            {partners?.items.map((partner) => (
              <RadioButtonCard
                config={getRadioConfig(partner)}
                name="partnersList"
                cardStyles={{
                  height: '84px',
                }}
              />
            ))}
          </div>
        </>
      )}
    </>
  );
};

const GetAPIKeys = () => {
  const [copiedID, setCopiedID] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const { payment } = usePayment();
  const { key } = payment;
  const clientID = key?.client_id;
  const clientSecret = key?.client_secret;

  useEffect(() => {
    if (copiedID) {
      setTimeout(() => {
        setCopiedID(false);
      }, 2000);
    }
    if (copiedSecret) {
      setTimeout(() => {
        setCopiedSecret(false);
      }, 2000);
    }
  }, [copiedID, copiedSecret]);

  const downloadKeysJSON = () => {
    if (!key?.client_id) return;

    const data = {
      client_id: key?.client_id,
      client_secret: key?.client_secret,
    };
    const dataStr = `data:text/json;charset=utf-8,${encodeURIComponent(
      JSON.stringify(data)
    )}`;
    downloadFile(dataStr, 'api_keys', 'json');
  };

  return (
    <div className="mt-1 mb-1 [&_*]:text-gray-blue-80">
      <div className="mt-12">Client ID</div>
      <div
        className={classNames(
          'flex mt-1',
          'h-9 w-full rounded-sm border border-gray-blue-20 bg-gray-blue-3 flex'
        )}
      >
        <div className="body-subtext py-2 px-3 whitespace-nowrap overflow-hidden text-ellipsis border-r border-gray-blue-20 text-gray-blue-80 flex-grow">
          {clientID}
        </div>
        <TooltipProvider>
          <Tooltip open={copiedID}>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="secondary"
                className="p-0 w-22 h-full border-0 py-2 pr-4 pl-3"
              >
                <CopyToClipboard
                  onCopy={() => setCopiedID(true)}
                  text={clientID}
                >
                  <div className="w-full h-full flex items-center justify-center">
                    <Icon
                      name="copy"
                      size={16}
                      className="m-0 text-gray-blue-50"
                    />
                    <span className="ml-1">Copy</span>
                  </div>
                </CopyToClipboard>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Copied!</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className="mt-5">Client secret</div>
      <div
        className={classNames(
          'flex mt-1',
          'h-9 w-full rounded-sm border border-gray-blue-20 bg-gray-blue-3 flex'
        )}
      >
        <div className="body-subtext py-2 px-3 whitespace-nowrap overflow-hidden text-ellipsis border-r border-gray-blue-20 text-gray-blue-80 flex-grow">
          {clientSecret}
        </div>
        <TooltipProvider>
          <Tooltip open={copiedSecret}>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="secondary"
                className="p-0 w-[88px] h-full border-0 py-2 pr-4 pl-3"
              >
                <CopyToClipboard
                  onCopy={() => setCopiedSecret(true)}
                  text={clientSecret}
                >
                  <div className="w-full h-full flex items-center justify-center">
                    <Icon
                      name="copy"
                      size={16}
                      className="m-0 text-gray-blue-50"
                    />
                    <span className="ml-1">Copy</span>
                  </div>
                </CopyToClipboard>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Copied!</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className="flex mt-1">
        <span>
          <Icon
            name="info"
            size={18}
            className="pr-2"
            style={{ paddingTop: '2px', color: '#808690' }}
          />
        </span>
        <span className="body-subtext-medium text-muted-foreground">
          Save and store this new key to a secure place, such as password
          manager or secret store. It is non-recoverable and you won’t be able
          to see it again.
          <button
            type="button"
            className="pl-1"
            style={{
              all: 'unset',
              color: 'var(--color-green-400)',
              cursor: 'pointer',
            }}
            onClick={downloadKeysJSON}
          >
            Download as JSON
          </button>
        </span>
      </div>
    </div>
  );
};

const CreateAPIKey = ({
  openModal,
  setModal,
  isPartner,
}: IProps): JSX.Element => {
  const [stage, setStage] = useState(STAGE.SELECT_TYPE);
  const [type, setType] = useState<APIType | null>(null);
  const [selectedPartner, setSelectedPartner] = useState<string | null>(null);
  const { toAddKey, toResetPayment, toFetchKeys, payment } = usePayment();
  const { partnerStores, toFetchPartnersList, toResetPartner } =
    usePartnerStores();
  const { partners } = partnerStores;
  const { error: keyError } = payment;

  const toggle = () => {
    setModal(false);
  };

  const createAPIKeysHandler = async () => {
    let request;

    if (type === APIType.PARTNERS) {
      const partnerSelected = partners?.items?.find(
        (p) => p.id === selectedPartner
      );
      request =
        type === APIType.PARTNERS
          ? ({
              partner: {
                id: partnerSelected?.id,
                name: partnerSelected?.name,
              },
            } as PAYMENTS.ICreateAPIKeys)
          : undefined;
    }
    const response = await toAddKey(request);
    if (response) {
      if (
        (type === APIType.PARTNERS && !keyError?.addKeyPartner) ||
        !keyError.addKeySelf
      )
        setStage(STAGE.GET_KEYS);
    }
  };

  const continueClickHandler = async () => {
    if (type === APIType.PARTNERS) {
      const response = await toFetchPartnersList();
      if (response) setStage(STAGE.LIST_PARTNERS);
    } else if (type === APIType.SELF) {
      createAPIKeysHandler();
    }
  };

  const CreateKeyButton = (
    <Button
      disabled={selectedPartner === null || Boolean(keyError?.addKeyPartner)}
      onClick={createAPIKeysHandler}
      type="submit"
      className="flex items-center"
    >
      <Icon name="plus" size={18} className="pr-1" />
      Create key
    </Button>
  );

  const ContinueButton = (
    <Button
      disabled={type === null}
      onClick={continueClickHandler}
      type="submit"
      className="flex items-center"
    >
      Continue
      <Icon name="arrow-right" size={18} className="pl-1" />
    </Button>
  );

  useEffect(() => {
    if (isPartner) {
      createAPIKeysHandler();
    }

    return () => {
      toResetPartner();
      toResetPayment();
      toFetchKeys();
    };
  }, []);

  useEffect(() => {
    if (
      keyError?.addKeySelf?.error_code === 'role_based_permission_denied' ||
      keyError?.addKeyPartner?.error_code === 'role_based_permission_denied'
    )
      toggle();
  }, [keyError]);

  return (
    <ModalContainer
      isOpen={openModal}
      title={
        stage === STAGE.GET_KEYS
          ? 'API key created successfully'
          : 'Create API Key'
      }
      onClose={toggle}
      testId="modal-createAPIKey"
      footer={
        <div className="flex">
          {stage === STAGE.SELECT_TYPE && (
            <>
              <Button onClick={toggle} variant="secondary">
                Cancel
              </Button>
              {ContinueButton}
            </>
          )}
          {stage === STAGE.LIST_PARTNERS && (
            <>
              <Button
                onClick={() => setStage(STAGE.SELECT_TYPE)}
                variant="secondary"
              >
                Back
              </Button>
              {CreateKeyButton}
            </>
          )}
          {stage === STAGE.GET_KEYS && (
            <>
              <Button onClick={toggle}>Close</Button>
            </>
          )}
        </div>
      }
    >
      <div className="[&>*]:text-gray-blue-70">
        {isPartner && keyError?.addKeySelf && errorBanner}
        {!isPartner && stage === STAGE.SELECT_TYPE && (
          <SelectKeyType type={type} setType={setType} />
        )}
        {!isPartner && stage === STAGE.LIST_PARTNERS && (
          <ListPartners
            selectedPartner={selectedPartner}
            setSelectedPartner={setSelectedPartner}
          />
        )}
        {(isPartner || stage === STAGE.GET_KEYS) && !keyError?.addKeySelf && (
          <GetAPIKeys />
        )}
      </div>
    </ModalContainer>
  );
};

export default CreateAPIKey;
